/* eslint-disable @typescript-eslint/no-explicit-any */
import { NgIf } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BoondockFacade } from '@curbnturf/boondock-listing';
import { SiteSvgsComponent } from '@curbnturf/custom-svgs/src/lib/site-svgs/site-svgs.component';
import { Activity, ActivityType, Boondock } from '@curbnturf/entities';
import { IActivityRatingForm } from '@curbnturf/form-helpers';
import { RatingFacade } from '@curbnturf/rating';
import * as RatingActions from '@curbnturf/rating/src/lib/+state/rating.actions';
import { LoadingController, NavController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subscription } from 'rxjs';
import { RatingAvailabilityToggleComponent } from '../rating-availability-toggle/rating-availability-toggle.component';

@Component({
  selector: 'curbnturf-rating-boondock-activities',
  templateUrl: './rating-boondock-activities.component.html',
  styleUrls: ['./rating-boondock-activities.component.scss'],
  standalone: true,
  imports: [FormsModule, NgIf, RatingAvailabilityToggleComponent, ReactiveFormsModule, SiteSvgsComponent],
})
export class RatingBoondockActivitiesComponent implements OnInit, OnDestroy {
  @Output() navigateBack: EventEmitter<void> = new EventEmitter<void>();

  @Output() continue: EventEmitter<any> = new EventEmitter<void>();

  @Output() skip: EventEmitter<any> = new EventEmitter<void>();

  subscriptions: Subscription[] = [];

  boondock$: Observable<Boondock | null>;
  boondockId: number | undefined;

  appContext: boolean;
  caretakerBoondock: number[];

  activitiesForm: FormGroup<IActivityRatingForm>;

  get isCaretaker() {
    return !!(this.boondockId && this.caretakerBoondock.includes(this.boondockId));
  }

  constructor(
    private updates$: Actions,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private loadingController: LoadingController,
    private navCtrl: NavController,
    private ratingFacade: RatingFacade,
    private boondockFacade: BoondockFacade,
  ) {}

  ngOnInit(): void {
    this.activitiesForm = this.fb.group<IActivityRatingForm>({
      biking: this.fb.control(''),
      boating: this.fb.control(''),
      fishing: this.fb.control(''),
      golf: this.fb.control(''),
      hiking: this.fb.control(''),
      horseback: this.fb.control(''),
      hunting: this.fb.control(''),
      kayaking: this.fb.control(''),
      motorSports: this.fb.control(''),
      ohv: this.fb.control(''),
      rafting: this.fb.control(''),
      rockClimbing: this.fb.control(''),
      snowSports: this.fb.control(''),
      surfing: this.fb.control(''),
      swimming: this.fb.control(''),
      waterSports: this.fb.control(''),
      wildlifeWatching: this.fb.control(''),
      windSports: this.fb.control(''),
    });

    this.boondock$ = this.boondockFacade.selected$;
    this.subscriptions.push(
      this.ratingFacade.caretakerSites$.subscribe((boondocks) => {
        this.caretakerBoondock = boondocks;
      }),
      this.boondock$.subscribe((boondock) => {
        if (boondock) {
          this.boondockId = boondock.id;
          this.populateFields(boondock);
        }
      }),
      this.route.data.subscribe((data: { appContext: boolean }) => {
        this.appContext = data.appContext || false;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  populateFields(boondock: Boondock) {
    if (this.isCaretaker) {
      this.activitiesForm.patchValue({
        biking: boondock.activities?.find((el) => el.type === 'biking') ? 'yes' : 'no',
        boating: boondock.activities?.find((el) => el.type === 'boating') ? 'yes' : 'no',
        fishing: boondock.activities?.find((el) => el.type === 'fishing') ? 'yes' : 'no',
        golf: boondock.activities?.find((el) => el.type === 'golf') ? 'yes' : 'no',
        hiking: boondock.activities?.find((el) => el.type === 'hiking') ? 'yes' : 'no',
        horseback: boondock.activities?.find((el) => el.type === 'horseback') ? 'yes' : 'no',
        hunting: boondock.activities?.find((el) => el.type === 'hunting') ? 'yes' : 'no',
        kayaking: boondock.activities?.find((el) => el.type === 'kayaking') ? 'yes' : 'no',
        motorSports: boondock.activities?.find((el) => el.type === 'motorSports') ? 'yes' : 'no',
        ohv: boondock.activities?.find((el) => el.type === 'ohv') ? 'yes' : 'no',
        rafting: boondock.activities?.find((el) => el.type === 'rafting') ? 'yes' : 'no',
        rockClimbing: boondock.activities?.find((el) => el.type === 'rockClimbing') ? 'yes' : 'no',
        snowSports: boondock.activities?.find((el) => el.type === 'snowSports') ? 'yes' : 'no',
        surfing: boondock.activities?.find((el) => el.type === 'surfing') ? 'yes' : 'no',
        swimming: boondock.activities?.find((el) => el.type === 'swimming') ? 'yes' : 'no',
        waterSports: boondock.activities?.find((el) => el.type === 'waterSports') ? 'yes' : 'no',
        wildlifeWatching: boondock.activities?.find((el) => el.type === 'wildlifeWatching') ? 'yes' : 'no',
        windSports: boondock.activities?.find((el) => el.type === 'windSports') ? 'yes' : 'no',
      });
    }
  }

  async onContinue() {
    const activities = [];

    for (const activityKey in this.activitiesForm.value) {
      if (this.activitiesForm.value.hasOwnProperty(activityKey)) {
        const activity = (this.activitiesForm.value as any)[activityKey];
        if (activity === 'yes' || activity === 'no') {
          activities.push(
            new Activity({
              type: activityKey as keyof typeof ActivityType,
              description: 'User response: ' + activity,
              boondock: { id: this.boondockId },
              boondockId: this.boondockId,
            }),
          );
        }
      }
    }

    const loading = await this.loadingController.create({
      message: 'Saving...',
      duration: 2000,
    });
    await loading.present();

    // Make the subscription before sending the request to make sure we don't miss anything.
    this.subscriptions.push(
      this.updates$.pipe(ofType(RatingActions.selectCaretakerBoondock)).subscribe(async () => {
        await loading.dismiss();

        // Emit event for web and navigate for app.
        if (this.appContext) {
          await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'amenities']);
        } else {
          this.continue.emit();
        }
      }),
    );

    this.ratingFacade.selectCaretakerBoondock({
      boondockId: this.boondockId,
      boondock: {
        id: this.boondockId,
        activities,
      },
    });
  }

  async onSkip() {
    // Emit event for web and navigate for app.
    if (this.appContext) {
      await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'amenities']);
    } else {
      this.skip.emit();
    }
  }
}
